import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, LogOut, Send, Trash2, Inbox, FileText } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "mca_admin_token";

const inputCls =
  "w-full bg-navy-950/70 border border-gold/20 rounded-xl px-4 py-3.5 text-sm text-ink placeholder:text-ink-muted focus:outline-none focus:border-gold/70 transition-colors duration-300";

const fmt = (iso) => new Date(iso).toLocaleString("en-KE", { dateStyle: "medium", timeStyle: "short" });

const GoogleMark = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#4285F4" d="M23.5 12.27c0-.85-.08-1.66-.22-2.45H12v4.64h6.45a5.52 5.52 0 0 1-2.39 3.62v3h3.87c2.26-2.09 3.57-5.16 3.57-8.81z"/>
    <path fill="#34A853" d="M12 24c3.24 0 5.96-1.07 7.94-2.92l-3.87-3c-1.07.72-2.44 1.15-4.07 1.15-3.13 0-5.78-2.11-6.73-4.96H1.29v3.1A12 12 0 0 0 12 24z"/>
    <path fill="#FBBC05" d="M5.27 14.27A7.2 7.2 0 0 1 4.89 12c0-.79.14-1.56.38-2.27v-3.1H1.29a12 12 0 0 0 0 10.74l3.98-3.1z"/>
    <path fill="#EA4335" d="M12 4.77c1.76 0 3.35.61 4.6 1.8l3.42-3.42A11.98 11.98 0 0 0 12 0 12 12 0 0 0 1.29 6.63l3.98 3.1C6.22 6.88 8.87 4.77 12 4.77z"/>
  </svg>
);

export default function AdminPage() {
  const location = useLocation();
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [googleUser, setGoogleUser] = useState(location.state?.user || null);
  const [checking, setChecking] = useState(!location.state?.user);
  const [login, setLogin] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("articles");
  const [articles, setArticles] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [draft, setDraft] = useState({ title: "", category: "Legal Updates", summary: "", body: "" });

  const authed = (config = {}) => ({
    withCredentials: true,
    ...config,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });

  const load = async () => {
    try {
      const [a, e] = await Promise.all([
        axios.get(`${API}/articles`),
        axios.get(`${API}/enquiries`, authed()),
      ]);
      setArticles(a.data);
      setEnquiries(e.data);
      return true;
    } catch {
      return false;
    }
  };

  useEffect(() => {
    const init = async () => {
      if (localStorage.getItem(TOKEN_KEY)) {
        const ok = await load();
        if (!ok) {
          localStorage.removeItem(TOKEN_KEY);
          setToken("");
        }
      } else if (!location.state?.user) {
        try {
          const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
          setGoogleUser(data);
          await load();
        } catch {
          /* not signed in */
        }
      } else {
        await load();
      }
      setChecking(false);
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const doLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, login);
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      await load();
      toast.success("Welcome back, Counsel.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  const doGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const publish = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/articles`, draft, authed());
      toast.success("Article published.");
      setDraft({ title: "", category: "Legal Updates", summary: "", body: "" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not publish");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/articles/${id}`, authed());
      toast.success("Article removed.");
      load();
    } catch {
      toast.error("Could not delete");
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch {
      /* cookie already gone */
    }
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setGoogleUser(null);
  };

  const isAuthed = Boolean(token || googleUser);

  if (checking) {
    return (
      <div className="min-h-screen bg-navy-950 flex items-center justify-center" data-testid="admin-loading">
        <Loader2 className="animate-spin text-gold" size={24} />
      </div>
    );
  }

  if (!isAuthed) {
    return (
      <div className="min-h-screen bg-navy-950 flex items-center justify-center px-6" data-testid="admin-login-page">
        <div className="w-full max-w-md rounded-3xl border border-gold/20 bg-navy-900/60 backdrop-blur-xl p-10">
          <div className="flex items-center gap-3 mb-8">
            <img src="/assets/logo.jpg" alt="Firm seal" className="h-10 w-10 rounded-full ring-1 ring-gold/50" />
            <div>
              <div className="font-display text-sm tracking-[0.15em]">CHAMBERS DESK</div>
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold">Restricted Access</div>
            </div>
          </div>

          <button
            data-testid="admin-google-login-button"
            onClick={doGoogleLogin}
            className="w-full flex items-center justify-center gap-3 bg-ink text-navy-950 font-medium py-3.5 rounded-full hover:bg-white transition-colors"
          >
            <GoogleMark />
            Sign in with Google
          </button>

          <div className="flex items-center gap-4 my-6">
            <span className="h-px flex-1 bg-gold/15" />
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink-muted">or with email</span>
            <span className="h-px flex-1 bg-gold/15" />
          </div>

          <form onSubmit={doLogin} className="space-y-5">
            <input
              data-testid="admin-email-input"
              type="email"
              required
              value={login.email}
              onChange={(e) => setLogin({ ...login, email: e.target.value })}
              placeholder="Email"
              className={inputCls}
            />
            <input
              data-testid="admin-password-input"
              type="password"
              required
              value={login.password}
              onChange={(e) => setLogin({ ...login, password: e.target.value })}
              placeholder="Password"
              className={inputCls}
            />
            <button
              data-testid="admin-login-button"
              disabled={busy}
              className="w-full bg-gold text-navy-950 font-medium py-3.5 rounded-full hover:bg-gold-hover transition-colors disabled:opacity-60"
            >
              {busy ? "Signing in…" : "Sign In"}
            </button>
          </form>
          <a href="/" className="block text-center text-xs text-ink-muted hover:text-gold transition-colors mt-6">
            &larr; Back to the website
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-navy-950 text-ink" data-testid="admin-dashboard">
      <header className="border-b border-gold/15 bg-navy-900/50 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <span className="font-display text-sm tracking-[0.15em]">CHAMBERS DESK</span>
          <div className="flex items-center gap-3">
            <button
              data-testid="admin-tab-articles"
              onClick={() => setTab("articles")}
              className={`flex items-center gap-2 text-xs px-4 py-2 rounded-full border transition-colors ${
                tab === "articles" ? "border-gold text-gold" : "border-ink/15 text-ink-dim hover:border-gold/40"
              }`}
            >
              <FileText size={14} /> Articles ({articles.length})
            </button>
            <button
              data-testid="admin-tab-enquiries"
              onClick={() => setTab("enquiries")}
              className={`flex items-center gap-2 text-xs px-4 py-2 rounded-full border transition-colors ${
                tab === "enquiries" ? "border-gold text-gold" : "border-ink/15 text-ink-dim hover:border-gold/40"
              }`}
            >
              <Inbox size={14} /> Enquiries ({enquiries.length})
            </button>
            <button
              data-testid="admin-logout-button"
              onClick={logout}
              className="flex items-center gap-2 text-xs px-4 py-2 rounded-full border border-ink/15 text-ink-dim hover:border-gold/40 hover:text-gold transition-colors"
            >
              <LogOut size={14} /> Sign Out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10">
        {tab === "articles" ? (
          <div className="grid lg:grid-cols-2 gap-8">
            <form
              onSubmit={publish}
              data-testid="article-publish-form"
              className="rounded-3xl border border-gold/20 bg-navy-900/50 p-8 space-y-4 h-fit"
            >
              <h2 className="font-display text-lg">Publish an Update</h2>
              <input
                data-testid="article-title-input"
                required
                value={draft.title}
                onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                placeholder="Article title"
                className={inputCls}
              />
              <input
                data-testid="article-category-input"
                value={draft.category}
                onChange={(e) => setDraft({ ...draft, category: e.target.value })}
                placeholder="Category (e.g. Tax Laws)"
                className={inputCls}
              />
              <textarea
                data-testid="article-summary-input"
                required
                rows={2}
                value={draft.summary}
                onChange={(e) => setDraft({ ...draft, summary: e.target.value })}
                placeholder="Short summary shown on the card…"
                className={`${inputCls} resize-none`}
              />
              <textarea
                data-testid="article-body-input"
                required
                rows={10}
                value={draft.body}
                onChange={(e) => setDraft({ ...draft, body: e.target.value })}
                placeholder="Full article text. Separate paragraphs with a blank line…"
                className={`${inputCls} resize-y`}
              />
              <button
                data-testid="article-publish-button"
                disabled={busy}
                className="w-full flex items-center justify-center gap-2 bg-gold text-navy-950 font-medium py-3.5 rounded-full hover:bg-gold-hover transition-colors disabled:opacity-60"
              >
                {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={15} />}
                {busy ? "Publishing…" : "Publish Article"}
              </button>
            </form>

            <div className="space-y-3">
              {articles.map((a) => (
                <div
                  key={a.id}
                  data-testid={`admin-article-${a.id}`}
                  className="rounded-2xl border border-gold/15 bg-navy-900/40 p-5 flex items-start justify-between gap-4"
                >
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold">{a.category}</div>
                    <div className="font-display mt-1">{a.title}</div>
                    <div className="font-mono text-xs text-ink-muted mt-1">{fmt(a.created_at)}</div>
                  </div>
                  <button
                    data-testid={`admin-article-delete-${a.id}`}
                    onClick={() => remove(a.id)}
                    className="text-ink-muted hover:text-red-400 transition-colors p-2"
                    aria-label="Delete article"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {enquiries.length === 0 && (
              <p className="text-ink-muted text-sm">No enquiries yet.</p>
            )}
            {enquiries.map((e) => (
              <div
                key={e.id}
                data-testid={`admin-enquiry-${e.id}`}
                className="rounded-2xl border border-gold/15 bg-navy-900/40 p-6"
              >
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                  <span className="font-display">{e.name}</span>
                  <a href={`mailto:${e.email}`} className="text-gold text-sm hover:underline">{e.email}</a>
                  {e.phone && <span className="text-ink-dim text-sm">{e.phone}</span>}
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold border border-gold/25 rounded-full px-3 py-0.5">
                    {e.practice_area || "General"}
                  </span>
                  <span className="font-mono text-xs text-ink-muted ml-auto">{fmt(e.created_at)}</span>
                </div>
                <p className="mt-3 text-sm text-ink-dim leading-relaxed whitespace-pre-wrap">{e.message}</p>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
